import React, { useEffect, useRef } from "react";
import { MdVisibility } from "react-icons/md";
import { GiSupersonicArrow, GiCutDiamond } from "react-icons/gi";
import { FaUserFriends, FaCalendarAlt, FaLightbulb, FaQuoteLeft, FaAngleRight, FaCode, FaBriefcase, FaGlobe, FaLinkedin, FaTwitter, FaGithub, FaArrowRight } from "react-icons/fa";
import "./About.css";
import Aos from "aos";
import "aos/dist/aos.css";
import { gsap } from 'gsap';
import collin from "../images/collin.jpeg";
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

export default function About() {
  const statsRef = useRef(null);
  const quoteRef = useRef(null);
  const headerContentRef = useRef(null);
  const aboutRef = useRef(null);
  const heroRef = useRef(null);
  const mvvSectionRef = useRef(null);
  const teamSectionRef = useRef(null);
  const statItemsRef = useRef([]);
  const mvvItemsRef = useRef([]);
  const teamItemsRef = useRef([]);
  const progressBarRef = useRef(null);
  
  useEffect(() => {
    Aos.init({
      duration: 1000,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      once: false,
    });
    
    // Animate stats on visibility
    const statsObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          startCountAnimation();
          statsObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });
    
    if (statsRef.current) {
      statsObserver.observe(statsRef.current);
    }
    
    // Parallax effect for quote
    const quoteObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && quoteRef.current) {
          quoteRef.current.classList.add('quote-visible');
        } else if (quoteRef.current) {
          quoteRef.current.classList.remove('quote-visible');
        }
      });
    }, { threshold: 0.2 });
    
    if (quoteRef.current) {
      quoteObserver.observe(quoteRef.current);
    }
    
    // Parallax effect for scroll
    const handleScroll = () => {
      if (headerContentRef.current) {
        const scrollY = window.scrollY;
        headerContentRef.current.style.transform = `translateY(${scrollY * 0.2}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Initialize animations
    initAnimations();
    
    // Initialize scroll progress bar
    initScrollProgress();
    
    // Initialize particle animation
    initParticleAnimation();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (statsRef.current) statsObserver.unobserve(statsRef.current);
      if (quoteRef.current) quoteObserver.unobserve(quoteRef.current);
      // Cleanup all ScrollTrigger instances when component unmounts
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);
  
  const startCountAnimation = () => {
    const statItems = document.querySelectorAll('.stat-number');
    
    statItems.forEach(item => {
      const target = parseInt(item.getAttribute('data-target'));
      let count = 0;
      const duration = 2000; // ms
      const frameDuration = 1000 / 60; // 60fps
      const totalFrames = Math.round(duration / frameDuration);
      const increment = target / totalFrames;
      
      const counter = setInterval(() => {
        count += increment;
        
        if (count >= target) {
          item.textContent = target + '+';
          clearInterval(counter);
        } else {
          item.textContent = Math.floor(count) + '+';
        }
      }, frameDuration);
    });
  };

  const initAnimations = () => {
    // Hero section animation
    gsap.fromTo(
      heroRef.current.querySelector('.about-header-content'),
      { y: 50, opacity: 0 },
      { 
        y: 0, 
        opacity: 1, 
        duration: 1.2, 
        ease: 'power3.out' 
      }
    );

    // Animate MVV section
    if (mvvSectionRef.current) {
      gsap.fromTo(
        mvvSectionRef.current.querySelector('.section-title'),
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: mvvSectionRef.current,
            start: 'top 80%',
          }
        }
      );

      // Stagger MVV cards animation
      gsap.fromTo(
        mvvSectionRef.current.querySelectorAll('.mvv-card'),
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.2,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: mvvSectionRef.current,
            start: 'top 70%',
          }
        }
      );
    }

    // Animate Team section
    if (teamSectionRef.current) {
      gsap.fromTo(
        teamSectionRef.current.querySelector('.section-title'),
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: teamSectionRef.current,
            start: 'top 80%',
          }
        }
      );

      // Stagger team cards animation
      gsap.fromTo(
        teamSectionRef.current.querySelectorAll('.team-card'),
        { y: 50, opacity: 0, scale: 0.95 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          stagger: 0.15,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: teamSectionRef.current,
            start: 'top 70%',
          }
        }
      );
    }

    // Statistics counter animation
    const statElements = document.querySelectorAll('.stat-number');
    statElements.forEach(stat => {
      const target = parseInt(stat.getAttribute('data-target'), 10);
      
      gsap.fromTo(
        stat,
        { innerHTML: '0' },
        {
          innerHTML: target,
          duration: 2,
          ease: 'power2.out',
          snap: { innerHTML: 1 },
          scrollTrigger: {
            trigger: stat,
            start: 'top 80%',
          }
        }
      );
    });
  };

  const initScrollProgress = () => {
    window.addEventListener('scroll', () => {
      if (progressBarRef.current) {
        const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
        const scrolled = (window.scrollY / windowHeight) * 100;
        progressBarRef.current.style.width = `${scrolled}%`;
      }
    });
  };

  const initParticleAnimation = () => {
    // This creates floating particles in the background
    const particles = document.querySelectorAll('.particle');
    particles.forEach(particle => {
      gsap.to(particle, {
        x: "random(-30, 30)",
        y: "random(-30, 30)",
        duration: "random(2, 6)",
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut"
      });
    });
  };

  return (
    <div className="about-container" ref={aboutRef}>
      {/* Progress Bar */}
      <div className="progress-container">
        <div className="progress-bar" ref={progressBarRef}></div>
      </div>
      
      {/* Header Section */}
      <div className="about-header" ref={heroRef}>
        <div className="container">
          <div className="about-header-content" ref={headerContentRef} data-aos="fade-up">
            <div className="badge">Our Story</div>
            <h1>About <span className="highlight">NamStack</span></h1>
            <p>Discover our journey, mission, and the passionate team behind Namibia's leading tech company</p>
          </div>
        </div>
        
        <div className="about-header-shapes">
          <svg viewBox="0 0 600 400" xmlns="http://www.w3.org/2000/svg" className="about-svg">
            <rect x="50" y="50" width="100" height="100" rx="15" fill="#2c3c5420" className="floating-shape" />
            <rect x="450" y="70" width="80" height="80" rx="15" fill="#942c2420" className="floating-shape-reverse" />
            <rect x="150" y="250" width="60" height="60" rx="15" fill="#2c3c5415" className="floating-shape-delayed" />
            <rect x="400" y="200" width="120" height="120" rx="15" fill="#2c3c5410" className="floating-shape-slow" />
            <circle cx="100" cy="350" r="40" fill="#942c2410" className="pulse-shape" />
            <circle cx="500" cy="300" r="30" fill="#2c3c5415" className="pulse-shape-slow" />
            <path d="M300,50 Q400,150 300,250 Q200,350 300,450" stroke="#2c3c5420" strokeWidth="5" fill="none" className="wave-path" />
            <path d="M320,30 Q420,130 320,230 Q220,330 320,430" stroke="#942c2410" strokeWidth="3" fill="none" className="wave-path-delayed" />
          </svg>
        </div>
      </div>
      
      {/* Quote Section */}
      <section className="about-quote section">
        <div className="container">
          <div className="quote-container" ref={quoteRef} data-aos="fade-up">
            <FaQuoteLeft className="quote-icon" />
            <blockquote>
              <p>Our mission at NamStack is to transform businesses through innovative technology solutions that are tailored to the unique needs of our clients, all while maintaining the highest standards of quality and excellence.</p>
              <footer>
                <cite>— Collin J Nehemia & Elifas S Andreas, Co-founders</cite>
              </footer>
            </blockquote>
          </div>
        </div>
      </section>
      
      {/* Story Section */}
      <section className="about-story section">
        <div className="about-story-bg"></div>
        <div className="container">
          <div className="about-story-grid">
            <div className="about-story-content" data-aos="fade-right">
              <div className="section-title left-align">
                <div className="section-subtitle">Who We Are</div>
                <h2>Our Journey</h2>
              </div>
              <div className="about-story-info">
                <div className="about-info-item">
                  <div className="about-info-icon">
                    <FaCalendarAlt />
                  </div>
                  <div className="about-info-text">
                    <h4>Founded in 2021</h4>
                    <p>Started our journey in March 2021</p>
                  </div>
                </div>
                <div className="about-info-item">
                  <div className="about-info-icon">
                    <FaUserFriends />
                  </div>
                  <div className="about-info-text">
                    <h4>Founded by Developers</h4>
                    <p>Collin J Nehemia & Elifas S Andreas</p>
                  </div>
                </div>
                <div className="about-info-item">
                  <div className="about-info-icon">
                    <FaLightbulb />
                  </div>
                  <div className="about-info-text">
                    <h4>Our Vision</h4>
                    <p>Filling the gap for quality software in Namibia</p>
                  </div>
                </div>
              </div>
              <p className="about-story-text">
                NamStack is a Namibian technology company founded in March 2021 by two passionate software developers: <strong>Collin J Nehemia</strong> and <strong>Elifas S Andreas</strong>. They joined forces after identifying a significant gap in the delivery of high-quality software products in Namibia.
              </p>
              <p className="about-story-text">
                With their combined expertise and shared vision, they established NamStack to provide exceptional technology solutions that meet international standards while addressing local needs and challenges unique to the Namibian market.
              </p>
              <a href="/contact" className="about-link">Connect with us <FaArrowRight className="btn-icon" /></a>
            </div>
            
            <div className="about-story-illustration" data-aos="fade-left">
              <div className="about-illustration-container">
                <div className="about-blob"></div>
                <div className="about-dev-illustration">
                  <svg viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg" className="developer-svg">
                    <g className="laptop">
                      <rect x="100" y="250" width="300" height="200" rx="10" fill="#2c3c5430" />
                      <rect x="120" y="270" width="260" height="160" rx="5" fill="#f5f5f5" />
                      <rect x="140" y="290" width="220" height="120" rx="2" fill="#fff" />
                      <rect x="160" y="310" width="60" height="10" rx="2" fill="#2c3c5440" className="code-line" />
                      <rect x="160" y="330" width="180" height="10" rx="2" fill="#2c3c5430" className="code-line" />
                      <rect x="160" y="350" width="140" height="10" rx="2" fill="#2c3c5420" className="code-line" />
                      <rect x="160" y="370" width="160" height="10" rx="2" fill="#2c3c5440" className="code-line" />
                      <rect x="240" y="450" width="20" height="5" rx="2" fill="#2c3c5470" />
                    </g>
                    <g className="person">
                      <circle cx="250" cy="180" r="50" fill="#2c3c5440" />
                      <rect x="200" y="230" width="100" height="80" rx="10" fill="#2c3c5440" />
                    </g>
                    <circle cx="160" cy="310" r="5" fill="#942c2440" className="particle" />
                    <circle cx="160" cy="350" r="5" fill="#2c3c5440" className="particle particle-delayed" />
                    <circle cx="160" cy="370" r="5" fill="#942c2440" className="particle particle-delayed-2" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Founders Section */}
      <section className="about-founders section">
        <div className="container">
          <div className="section-title center-align" data-aos="fade-up">
            <div className="section-subtitle">Meet Our Founders</div>
            <h2>The Visionaries Behind NamStack</h2>
            <p>Get to know the innovative minds who started it all</p>
          </div>
          
          <div className="founders-grid">
            <div className="founder-card" data-aos="fade-up" data-aos-delay="100">
              <div className="founder-img-container">
                <div className="founder-img founder-1"></div>
                <div className="founder-img-overlay">
                  <div className="founder-social">
                    <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer">
                      <FaLinkedin />
                    </a>
                    <a href="https://twitter.com" target="_blank" rel="noopener noreferrer">
                      <FaTwitter />
                    </a>
                    <a href="https://github.com" target="_blank" rel="noopener noreferrer">
                      <FaGithub />
                    </a>
                  </div>
                </div>
              </div>
              <div className="founder-info">
                <h3>Collin J Nehemia</h3>
                <div className="founder-role">Co-Founder & Technical Lead</div>
                <p>A passionate software architect with expertise in scalable systems and cloud infrastructure. Collin brings over 8 years of development experience and a vision for technical excellence.</p>
                <div className="founder-specialties">
                  <span>Full-Stack Development</span>
                  <span>System Architecture</span>
                  <span>Cloud Solutions</span>
                </div>
              </div>
            </div>

            <div className="founder-card" data-aos="fade-up" data-aos-delay="200">
              <div className="founder-img-container">
                <div className="founder-img founder-2"></div>
                <div className="founder-img-overlay">
                  <div className="founder-social">
                    <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer">
                      <FaLinkedin />
                    </a>
                    <a href="https://twitter.com" target="_blank" rel="noopener noreferrer">
                      <FaTwitter />
                    </a>
                    <a href="https://github.com" target="_blank" rel="noopener noreferrer">
                      <FaGithub />
                    </a>
                  </div>
                </div>
              </div>
              <div className="founder-info">
                <h3>Elifas S Andreas</h3>
                <div className="founder-role">Co-Founder & Product Lead</div>
                <p>A strategic thinker with a keen eye for user experience and product development. Elifas combines technical expertise with business acumen to deliver solutions that exceed client expectations.</p>
                <div className="founder-specialties">
                  <span>Product Strategy</span>
                  <span>UX Design</span>
                  <span>Tech Innovation</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="founders-bg-shapes">
          <svg viewBox="0 0 600 400" xmlns="http://www.w3.org/2000/svg" className="founders-svg">
            <path d="M50,200 Q150,50 250,200 T450,200" stroke="#2c3c5410" strokeWidth="2" fill="none" className="wave-path" />
            <path d="M100,250 Q200,100 300,250 T500,250" stroke="#942c2410" strokeWidth="2" fill="none" className="wave-path-delayed" />
            <circle cx="150" cy="150" r="30" fill="#2c3c5408" className="floating-shape" />
            <circle cx="450" cy="250" r="40" fill="#942c2408" className="floating-shape-reverse" />
            <rect x="300" y="100" width="60" height="60" rx="15" fill="#2c3c5408" className="floating-shape-delayed" />
          </svg>
        </div>
      </section>
      
      {/* Stats Section */}
      <section className="about-stats section" ref={statsRef}>
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card" data-aos="fade-up" data-aos-delay="100">
              <div className="stat-icon">
                <FaCode />
              </div>
              <div className="stat-number" data-target="100">0+</div>
              <div className="stat-title">Projects Completed</div>
            </div>
            
            <div className="stat-card" data-aos="fade-up" data-aos-delay="200">
              <div className="stat-icon">
                <FaBriefcase />
              </div>
              <div className="stat-number" data-target="50">0+</div>
              <div className="stat-title">Happy Clients</div>
            </div>
            
            <div className="stat-card" data-aos="fade-up" data-aos-delay="300">
              <div className="stat-icon">
                <FaGlobe />
              </div>
              <div className="stat-number" data-target="10">0+</div>
              <div className="stat-title">Industry Sectors</div>
            </div>
            
            <div className="stat-card" data-aos="fade-up" data-aos-delay="400">
              <div className="stat-icon">
                <FaUserFriends />
              </div>
              <div className="stat-number" data-target="15">0+</div>
              <div className="stat-title">Team Members</div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Mission Vision Values */}
      <section className="about-mvv section" ref={mvvSectionRef}>
        <div className="container">
          <div className="section-title" data-aos="fade-up">
            <div className="section-subtitle">Our Guiding Principles</div>
            <h2>Mission, Vision & Values</h2>
            <p>The core principles that drive us forward</p>
          </div>
          
          <div className="mvv-cards">
            <div className="mvv-card" data-aos="fade-up" data-aos-delay="100">
              <div className="mvv-icon">
                <GiSupersonicArrow />
              </div>
              <h3>Mission</h3>
              <p>Quality delivery of products and clients' satisfaction through innovative technology solutions tailored to their specific needs.</p>
            </div>
            
            <div className="mvv-card" data-aos="fade-up" data-aos-delay="200">
              <div className="mvv-icon">
                <MdVisibility />
              </div>
              <h3>Vision</h3>
              <p>Leading customers to a digitalized world through quality delivery, exceptional user experiences, and cutting-edge technology.</p>
            </div>
            
            <div className="mvv-card" data-aos="fade-up" data-aos-delay="300">
              <div className="mvv-icon">
                <GiCutDiamond />
              </div>
              <h3>Values</h3>
              <ul className="values-list">
                <li>Excellence in everything we do</li>
                <li>Innovation and continuous improvement</li>
                <li>Integrity and transparency</li>
                <li>Customer-centric approach</li>
                <li>Teamwork and collaboration</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="mvv-wave">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path fill="#ffffff" fillOpacity="1" d="M0,256L48,240C96,224,192,192,288,186.7C384,181,480,203,576,224C672,245,768,267,864,250.7C960,235,1056,181,1152,165.3C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
      </section>
      
      {/* Team Section */}
      <section className="about-team section" ref={teamSectionRef}>
        <div className="container">
          <div className="section-title" data-aos="fade-up">
            <div className="section-subtitle">Meet Our Team</div>
            <h2>The Founders</h2>
            <p>The creative minds behind NamStack</p>
          </div>
          
          <div className="team-grid">
            <div className="team-card" data-aos="fade-up" data-aos-delay="100">
              <div className="team-img-container">
                <img src={collin} alt="Collin J Nehemia" className="team-img team-img-1" />
                <div className="team-img-overlay"></div>
              </div>
              <div className="team-info">
                <h3>Collin J Nehemia</h3>
                <div className="team-role">Co-founder & CEO</div>
                <p>Full-stack developer with expertise in modern web technologies and blockchain solutions.</p>
                <div className="team-social">
                  <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer"><FaLinkedin /></a>
                  <a href="https://twitter.com" target="_blank" rel="noopener noreferrer"><FaTwitter /></a>
                  <a href="https://github.com" target="_blank" rel="noopener noreferrer"><FaGithub /></a>
                </div>
              </div>
            </div>
            
            <div className="team-card" data-aos="fade-up" data-aos-delay="200">
              <div className="team-img-container">
              <img src={collin} alt="Elifas S Andreas" className="team-img team-img-2" />
                <div className="team-img-overlay"></div>
              </div>
              <div className="team-info">
                <h3>Elifas S Andreas</h3>
                <div className="team-role">Co-founder & CTO</div>
                <p>Expert in mobile development, UI/UX design, and software architecture.</p>
                <div className="team-social">
                  <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer"><FaLinkedin /></a>
                  <a href="https://twitter.com" target="_blank" rel="noopener noreferrer"><FaTwitter /></a>
                  <a href="https://github.com" target="_blank" rel="noopener noreferrer"><FaGithub /></a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="team-cta" data-aos="fade-up">
            <p>Interested in joining our team?</p>
            <a href="/contact" className="btn-primary">
              Get in Touch <FaArrowRight className="btn-icon" />
            </a>
          </div>
        </div>
        
        <div className="team-bg-shape team-bg-shape-1"></div>
        <div className="team-bg-shape team-bg-shape-2"></div>
      </section>
    </div>
  );
}
